import React from 'react';
import { Button, Dropdown, Tag } from 'antd';

import { KEBAB } from '../icons';
import GroupHeader from '../common/GroupHeader';
import PublishedBy from '../publishers/PublishedBy';
import { versionChip } from '../../utils/bundleGroups';
import { installedWords, isPartial } from '../../utils/packageInstalls';

/**
 * One installed Discover bundle in a collection's Card Library (KPR-1130;
 * epic KPE-035): a collapsible group between *In this collection* and *From
 * {parent}*, open by default.
 *
 * The header is the platform's `GroupHeader` (a micro-label, a rule, the
 * count) and says, in the label: the bundle's name, who published it, its
 * version (`v5`), how much of it is here (*Installed*, or *Installed (6 of 7
 * cards)* when some were deleted or kept; keepr-api KPR-1135), and one of two
 * states: *Update available* for a manager
 * when the collection is behind (the screen's one accent) or *Retired* when
 * Discover no longer lists it. A manager's ⋯ sits at its right end; a reader
 * sees the group with no ⋯ and no *Update available* (the API tells a reader
 * neither). Inside: the core's cards, then each add-on's under its name.
 *
 * `group` is `groupBundleRows`' entry; `showUpdate` says the person may see
 * the update state (a manager, in a collection that takes changes); `menu` an antd menu from
 * `buildActionMenu` (null: no ⋯); `renderRow` draws one card row, so a card in
 * a group keeps every action it has anywhere else.
 */
export default function BundleGroup({ group, open, onToggle, menu, renderRow, showUpdate = false }) {
	const { bundle, core, addOns, count } = group;
	const version = versionChip(bundle);
	const behind = showUpdate && bundle.updateAvailable === true && !bundle.retired;
	const label = (
		<span className='bundle-group__label'>
			<span className='bundle-group__name'>{bundle.name}</span>
			{bundle.publisher ? <PublishedBy publisher={bundle.publisher} className='bundle-group__meta' /> : null}
			{version ? (
				<Tag className='bundle-group__chip' title={`Installed at version ${bundle.version}`}>{version}</Tag>
			) : null}
			{bundle.state ? (
				<Tag
					className='bundle-group__chip bundle-group__chip--state'
					title={isPartial(bundle) ? 'Some of this bundle\'s cards were deleted or kept as your own' : 'Every card this bundle brought is here'}
				>
					{installedWords(bundle)}
				</Tag>
			) : null}
			{behind ? <Tag className='bundle-group__chip bundle-group__chip--update'>Update available</Tag> : null}
			{bundle.retired ? <Tag className='bundle-group__chip' title='Discover no longer lists this bundle'>Retired</Tag> : null}
		</span>
	);
	const action = menu ? (
		<Dropdown trigger={[ 'click' ]} placement='bottomRight' menu={menu}>
			<Button
				type='text'
				className='card-library__action bundle-group__menu'
				aria-label={`${ bundle.name } bundle actions`}
				icon={<KEBAB className='medium-icon' />}
			/>
		</Dropdown>
	) : null;

	return (
		<div className='card-library__section bundle-group' data-testid='card-library-bundle' data-bundle={bundle.key}>
			<GroupHeader
				as='h4'
				className='bundle-group__header'
				label={label}
				count={count}
				countLabel='card'
				collapsible
				collapsed={! open}
				onToggle={onToggle}
				action={action}
			/>
			{ open ? (
				<>
					{ core.length ? (
						<div className='card-library__grid'>{ core.map( renderRow ) }</div>
					) : null }
					{ addOns.map( addOn => (
						<div key={addOn.key} className='bundle-group__add-on' data-testid='card-library-bundle-add-on'>
							<h5 className='bundle-group__add-on-name'>{addOn.name}</h5>
							<div className='card-library__grid'>{ addOn.rows.map( renderRow ) }</div>
						</div>
					) ) }
				</>
			) : null }
		</div>
	);
}
